import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Divider,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AllTemplates from "./AllTemplates";
import TemplateSendPreview from "./TemplateSendPreview";
import { SearchNormal1, CloseCircle, InfoCircle, DocumentText } from "iconsax-react";

/**
 * TemplateSend
 * - The first dialog that displays a list of templates (AllTemplates) & a preview
 */
const TemplateSend = ({ open, onClose, onTemplateSelected }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { accountid } = useParams();

  // Nothing to send at all: the fetch finished and the account has no APPROVED template
  // (same "approved only" rule AllTemplates.jsx uses for its list). Not shown while loading
  // or on a failed fetch, where "you have no templates" would be misleading.
  const { templates, status } = useSelector((state) => state.template);
  const hasNoApprovedTemplates =
    status === "succeeded" && !(templates || []).some((t) => t.status === "APPROVED");

  const goToTemplates = () => {
    onClose?.();
    if (accountid) navigate(`/home/${accountid}/templates`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isSmDown}
      fullWidth
      // Slightly bigger than the original "md"(900px)/640px — went to "xl"
      // on the 2nd bump which Rahim flagged as "waaay to big", settled back
      // to "lg" (2026-08-24).
      maxWidth="lg"
      scroll="paper"
      sx={{
        "& .MuiDialog-paper": {
          padding: 0,
          margin: isSmDown ? 0 : undefined,
          maxHeight: isSmDown ? "100%" : "min(92dvh, 800px)",
          height: isSmDown ? "100%" : "min(92dvh, 720px)",
          overflow: "hidden",
          borderRadius: isSmDown ? 0 : 3,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 0,
          overflow: "hidden",
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Header — icon + "Templates" heading, close X on the same row.
            Was just a lone close button with no heading at all (Rahim,
            2026-08-24: "didnt add a heading of template"). */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1.25, sm: "20px" },
            pt: { xs: 0.75, sm: 1.25 },
            pb: { xs: 0.75, sm: 1 },
          }}
        >
          {/* Icon removed (Rahim, 2026-08-24: "remove the icon beside the
              templates heading"). Font flattened to the Section Title token
              (1rem/600, no per-breakpoint bump) — see font-sizing-scale.md,
              same size QuickReplyAutocomplete/ChatActionBox headers use. */}
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: "text.primary" }}>
            Templates
          </Typography>
          <IconButton size="small" aria-label="Close dialog" onClick={onClose}>
            <CloseCircle size={isSmDown ? 18 : 20} color="currentColor" variant="Linear" />
          </IconButton>
        </Box>
        <Divider />

        {/* List + preview: stacked on xs, side-by-side from sm */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 2 },
            overflow: "hidden",
            minHeight: 0,
            p: { xs: 1.25, sm: "20px" },
          }}
        >
          {/* One bordered card holding BOTH the search bar and the template
              rows together — was two disconnected pieces (search sat above
              this whole row, the list pane had only a borderRight) with
              nothing visually tying them into a single section (Rahim,
              2026-08-24: "make a box and add the search bar in it and the
              content cards as well"). */}
          <Box
            sx={{
              // Wider than the preview pane on desktop — was an even 1:1 split
              // (Rahim, 2026-08-24: "make it width a little more").
              flex: isMdDown ? "1 1 42%" : "1 1 60%",
              minHeight: 0,
              maxHeight: isMdDown ? "min(46vh, 360px)" : "none",
              display: "flex",
              flexDirection: "column",
              border: (t) => `1px solid ${t.palette.divider}`,
              borderRadius: "10px",
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <Box sx={{ p: { xs: 1.25, sm: 1.75 }, flexShrink: 0 }}>
              <TextField
                placeholder="Search templates"
                variant="outlined"
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchNormal1 size={18} color={theme.palette.text.secondary} variant="Linear" aria-hidden />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontSize: { xs: "0.8125rem", sm: "0.9375rem" },
                    "& fieldset": {
                      borderColor: (t) => t.palette.divider,
                    },
                  },
                }}
              />
            </Box>
            <Divider />
            {/* AllTemplates.jsx owns its own scroll now (a visible slim
                scrollbar, per Rahim, 2026-08-24: "vertical scroll bar is
                important so add vertical scroll bar") — this wrapper just
                needs to be a bounded flex box, not a second scroll container,
                or it'd double up into two nested scrollbars. */}
            <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
              <AllTemplates
                searchQuery={searchQuery}
                onTemplateSelected={onTemplateSelected}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: isMdDown ? "1 1 58%" : "1 1 40%",
              minHeight: isMdDown ? 200 : 0,
              overflow: "auto",
              WebkitOverflowScrolling: "touch",
              // Column + the note's own mt:"auto" below pins the note to the
              // bottom of this pane (level with the left card's bottom edge)
              // instead of sitting right up against the preview card with a
              // big empty gap underneath (Rahim, 2026-08-24: "add it in the
              // bottom right were the left card ends").
              display: "flex",
              flexDirection: "column",
            }}
          >
            {hasNoApprovedTemplates ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  px: 4,
                  py: 3,
                }}
              >
                {/* Same layout as Inbox's "No conversation selected" empty state (ChatBody.jsx) and
                    the same green button as its "Connect a channel" one; the icon is the app's own
                    Templates icon (LeftNav's "Templates" item uses DocumentText too). */}
                <Box sx={{ mb: 1.5, color: (t) => t.palette.text.secondary, display: "flex" }}>
                  <DocumentText size={64} color="currentColor" variant="Linear" />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "1.05rem", color: "text.primary", mb: 0.5 }}>
                  No templates yet
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", textAlign: "center", maxWidth: 340, mb: 2 }}>
                  The 24-hour window has expired, so you can only send an approved template. Create one to reach this customer.
                </Typography>
                <Button
                  variant="contained"
                  onClick={goToTemplates}
                  sx={{
                    px: 2.5,
                    py: 1,
                    backgroundColor: (t) => (t.palette.mode === "dark" ? "#4FC389" : "#44B57C"),
                    color: "#ffffff",
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    fontFamily: "Instrument Sans, sans-serif",
                    boxShadow: (t) => (t.palette.mode === "dark" ? "none" : "0 2px 8px rgba(68,181,124,0.25)"),
                    "&:hover": { backgroundColor: (t) => (t.palette.mode === "dark" ? "#3aad77" : "#38a06c") },
                  }}
                >
                  Go to Templates
                </Button>
              </Box>
            ) : (
              <>
                <TemplateSendPreview />
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mt: "auto", pt: 2, mb: 3, px: { xs: 1, sm: 0 } }}>
                  <InfoCircle size={16} color={theme.palette.text.secondary} variant="Linear" style={{ flexShrink: 0, marginTop: 2 }} />
                  <Typography sx={{ fontSize: "0.75rem", lineHeight: 1.5, color: "text.secondary" }}>
                    This is a preview of how the selected template will appear in your chat. Variables like {"{{1}}"}, {"{{2}}"} will be replaced dynamically.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSend;
